import type { StateCreator } from 'zustand';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { showErrorToastAndLog } from 'services/toaster';
import { issueClient } from 'shared/domain/issue/issueClient';
import type { IssueEventModel, IssueModel } from 'shared/domain/issue/issueModel';
import type { LinkedDocumentationItem } from 'shared/domain/issue/linkedDocumentation';
import type { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { createLatestRead } from 'store/latestRead';
import {
  addOrReplaceUserAccess,
  changeIssueEvent,
  CURRENT_ISSUE_RESYNC_THROTTLE_MS,
  shouldApplyResyncedIssue,
} from './model';

/** The issue of the issue view: read once opened, followed while open, changed optimistically by the view. */
export type CurrentIssueSlice = {
  currentIssueId: string | undefined;
  currentIssue: IssueModel | undefined;
  currentIssueLoading: boolean;
  currentIssueError: boolean;
  /** Ids of the events being sent; the view shows them as loading until the server's issue comes. */
  eventsBeingSaved: string[];
  /** Reads the issue and follows its changes; returns the close (resets). */
  openCurrentIssue: (issueId: string) => () => void;
  /** Reads the issue again: at most once per throttle time, unless forced. */
  resyncCurrentIssue: (options?: { force?: boolean }) => void;
  // Local changes name their issue: one that ends after the view moved to another issue is dropped.
  /** An event being created (with a temporary id from `createUniqueId`), shown as loading until it is saved. */
  addEventBeingSaved: (issueId: string, event: IssueEventModel) => void;
  /** An event being edited, shown with its changes as loading until it is saved. */
  markEventBeingSaved: (issueId: string, eventId: string, changes: Partial<IssueEventModel>) => void;
  markEventRemoved: (issueId: string, eventId: string) => void;
  /** An access the server saved (granted, changed or revoked). */
  saveUserAccessLocally: (issueId: string, access: UserAccess) => void;
  setLinkedDocumentation: (issueId: string, linkedDocumentation: LinkedDocumentationItem[]) => void;
  resetCurrentIssue: () => void;
};

type CurrentIssueState = Pick<
  CurrentIssueSlice,
  'currentIssueId' | 'currentIssue' | 'currentIssueLoading' | 'currentIssueError' | 'eventsBeingSaved'
>;

const initialState: CurrentIssueState = {
  currentIssueId: undefined,
  currentIssue: undefined,
  currentIssueLoading: false,
  currentIssueError: false,
  eventsBeingSaved: [],
};

export const createCurrentIssueSlice: StateCreator<CurrentIssueSlice> = (set, get) => {
  const latestRead = createLatestRead();
  let lastResyncAt = 0;

  /** Changes the current issue if it is the given one; tells whether it did. */
  const changeCurrentIssue = (
    issueId: string,
    change: (issue: IssueModel) => Partial<IssueModel>
  ): boolean => {
    const { currentIssue } = get();
    if (!currentIssue || !areObjectIdsEqual(currentIssue._id, issueId)) {
      return false;
    }
    set({ currentIssue: { ...currentIssue, ...change(currentIssue) } });
    return true;
  };

  const resetCurrentIssue = (): void => {
    latestRead.cancel();
    lastResyncAt = 0;
    set(initialState);
  };

  const resyncCurrentIssue: CurrentIssueSlice['resyncCurrentIssue'] = (options) => {
    const issueId = get().currentIssueId;
    const force = Boolean(options?.force);
    if (!issueId || (!force && Date.now() - lastResyncAt < CURRENT_ISSUE_RESYNC_THROTTLE_MS)) {
      return;
    }
    lastResyncAt = Date.now();
    set({ currentIssueError: false });
    void latestRead.run(() => issueClient.getOneById(issueId), {
      onResult: (resynced) => {
        const { currentIssue, eventsBeingSaved } = get();
        const hasEventsBeingSaved = eventsBeingSaved.length > 0;
        // A resync may supersede the first read, so it ends the loading too.
        set({ currentIssueLoading: false });
        if (
          areObjectIdsEqual(resynced._id, issueId) &&
          shouldApplyResyncedIssue(resynced, currentIssue, { force, hasEventsBeingSaved })
        ) {
          set({ currentIssue: resynced, eventsBeingSaved: [] });
        }
      },
      onError: (error) => {
        showErrorToastAndLog('Error while resyncing issue', error);
        set({ currentIssueError: true, currentIssueLoading: false });
      },
    });
  };

  return {
    ...initialState,
    openCurrentIssue: (issueId) => {
      resetCurrentIssue();
      set({ currentIssueId: issueId, currentIssueLoading: true });
      void latestRead.run(() => issueClient.getOneById(issueId), {
        onResult: (currentIssue) => set({ currentIssue, currentIssueLoading: false }),
        onError: (error) => {
          showErrorToastAndLog('Error while getting issue', error);
          set({ currentIssueError: true, currentIssueLoading: false });
        },
      });
      const stopFollowingChanges = issueClient.onEvent((event) => {
        if (event.type === DomainMessagesTypes.tableChanged) {
          get().resyncCurrentIssue();
        }
        if (
          event.type === DomainMessagesTypes.updatedEntity &&
          event.data &&
          areObjectIdsEqual(event.data._id, issueId)
        ) {
          // An edit the server saved: shown at once, then read what else changed.
          set({ currentIssue: event.data });
          get().resyncCurrentIssue();
        }
      });
      return (): void => {
        stopFollowingChanges();
        resetCurrentIssue();
      };
    },
    resyncCurrentIssue,
    addEventBeingSaved: (issueId, event) => {
      if (changeCurrentIssue(issueId, (issue) => ({ events: [...issue.events, event] }))) {
        set({ eventsBeingSaved: [...get().eventsBeingSaved, event._id] });
      }
    },
    markEventBeingSaved: (issueId, eventId, changes) => {
      if (changeCurrentIssue(issueId, (issue) => ({ events: changeIssueEvent(issue.events, eventId, changes) }))) {
        set({ eventsBeingSaved: [...get().eventsBeingSaved, eventId] });
      }
    },
    markEventRemoved: (issueId, eventId) => {
      changeCurrentIssue(issueId, (issue) => ({
        events: changeIssueEvent(issue.events, eventId, { deleted: true }),
      }));
    },
    saveUserAccessLocally: (issueId, access) => {
      changeCurrentIssue(issueId, (issue) => ({
        userAccesses: addOrReplaceUserAccess(issue.userAccesses, access),
      }));
    },
    setLinkedDocumentation: (issueId, linkedDocumentation) => {
      changeCurrentIssue(issueId, () => ({ linkedDocumentation }));
    },
    resetCurrentIssue,
  };
};
